<template>
  <div class="company-energy-card">
    <div class="company-energy-card__main">
      {{ displayTotal }}
    </div>

    <div class="company-energy-card__legend">
      <div
          v-for="item in normalizedLevels"
          :key="item.key"
          class="company-energy-card__legend-row"
      >
        <div class="company-energy-card__legend-name">
          <span
              class="company-energy-card__legend-mark"
              :style="{ backgroundColor: item.legendColor }"
          ></span>

          <span>{{ item.label }}</span>
        </div>

        <span
            class="company-energy-card__legend-value"
            :class="{
            'company-energy-card__legend-value--underlined': item.value !== 0
          }"
        >
          {{ item.value }} чел.
        </span>
      </div>
    </div>

    <div
        class="company-energy-card__bar"
        :style="reversedBarGridStyle"
    >
      <template v-if="reversedBarSegments.length">
        <div
            v-for="(item, index) in reversedBarSegments"
            :key="item.key"
            class="company-energy-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </template>
    </div>

    <div
        class="company-energy-card__bar-values"
        :style="reversedBarGridStyle"
    >
      <span
          v-for="item in reversedBarSegments"
          :key="item.key"
          class="company-energy-card__bar-value"
      >
        {{ item.value }}
      </span>
    </div>

    <div
        ref="chartWrapper"
        class="company-energy-card__chart"
    >
      <canvas ref="chartCanvas"></canvas>

      <div
          v-if="!hasChartData"
          class="company-energy-card__chart-empty"
      >
        Не хватает данных за текущий период
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import dayjs from 'dayjs'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
)

const GREY_COLOR = '#D1D1D1'

export default {
  name: 'CompanyEnergyCard',

  props: {
    total: {
      type: Number,
      default: null,
    },
    levels: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartValues: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartWrapper = ref(null)

    let chartInstance = null
    let resizeObserver = null
    let resizeFrame = null

    const hasValidTotal = computed(() => {
      return Number.isFinite(Number(props.total))
    })

    const safeTotal = computed(() => {
      const value = Number(props.total)
      return Number.isFinite(value) ? value : 0
    })

    const normalizedLevels = computed(() => {
      return props.levels.map((item) => {
        const value = Number(item.value)

        return {
          ...item,
          value: Number.isFinite(value) ? value : 0,
          legendColor:
              Number.isFinite(value) && value === 0 ? GREY_COLOR : item.color,
        }
      })
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, item) => sum + item.value, 0)
    })

    const allLevelsAreZero = computed(() => {
      return (
          normalizedLevels.value.length > 0 &&
          normalizedLevels.value.every((item) => item.value === 0)
      )
    })

    const barSegments = computed(() => {
      if (!normalizedLevels.value.length) {
        return []
      }

      if (allLevelsAreZero.value) {
        return normalizedLevels.value.map((item) => ({
          ...item,
          color: GREY_COLOR,
          ratio: 1,
        }))
      }

      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter((item) => item.value > 0)
          .map((item) => ({
            ...item,
            ratio: item.value,
          }))
    })

    const reversedBarSegments = computed(() => {
      return [...barSegments.value].reverse()
    })

    const reversedBarGridStyle = computed(() => {
      if (!reversedBarSegments.value.length) {
        return {}
      }

      return {
        gridTemplateColumns: reversedBarSegments.value
            .map((item) => `${item.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === reversedBarSegments.value.length - 1
      const currentItem = reversedBarSegments.value[index]

      return {
        backgroundColor: currentItem.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    const normalizedChartLabels = computed(() => {
      if (Array.isArray(props.chartLabels) && props.chartLabels.length) {
        return props.chartLabels
      }

      return []
    })

    const normalizedChartValues = computed(() => {
      return props.chartValues.map((value) => {
        const numericValue = Number(value)
        return Number.isFinite(numericValue) ? numericValue : null
      })
    })

    const hasChartData = computed(() => {
      const validValues = normalizedChartValues.value.filter(
          (value) => value !== null
      )
      return validValues.length >= 2
    })

    const displayTotal = computed(() => {
      return hasValidTotal.value ? `${safeTotal.value}%` : '-%'
    })

    const chartDatasetValues = computed(() => {
      if (!hasChartData.value) {
        return normalizedChartLabels.value.map(() => null)
      }

      return normalizedChartValues.value
    })

    const getChartGradient = (ctx, chartArea) => {
      const gradient = ctx.createLinearGradient(
          0,
          chartArea.top,
          0,
          chartArea.bottom
      )

      gradient.addColorStop(0, 'rgba(33, 172, 12, 0.28)')
      gradient.addColorStop(1, 'rgba(33, 172, 12, 0)')

      return gradient
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const forceResizeChart = () => {
      if (!chartInstance || !chartWrapper.value) {
        return
      }

      const width = chartWrapper.value.clientWidth
      const height = chartWrapper.value.clientHeight

      if (!width || !height) {
        return
      }

      chartInstance.resize(width, height)
      chartInstance.update('none')
    }

    const scheduleResize = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
      }

      resizeFrame = requestAnimationFrame(() => {
        forceResizeChart()
      })
    }

    const createChart = () => {
      if (!chartCanvas.value) {
        return
      }

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: normalizedChartLabels.value,
          datasets: [
            {
              data: chartDatasetValues.value,
              borderColor: hasChartData.value ? '#21AC0C' : 'rgba(0,0,0,0)',
              backgroundColor: (context) => {
                if (!hasChartData.value) {
                  return 'rgba(0,0,0,0)'
                }

                const chart = context.chart
                const { chartArea } = chart

                if (!chartArea) {
                  return 'rgba(33, 172, 12, 0.14)'
                }

                return getChartGradient(chart.ctx, chartArea)
              },
              borderWidth: hasChartData.value ? 1 : 0,
              pointRadius: hasChartData.value ? 3 : 0,
              pointHoverRadius: hasChartData.value ? 4 : 0,
              pointBackgroundColor: hasChartData.value
                  ? '#1C960A'
                  : 'rgba(0,0,0,0)',
              pointBorderColor: hasChartData.value
                  ? '#1C960A'
                  : 'rgba(0,0,0,0)',
              fill: hasChartData.value,
              tension: 0.25,
              spanGaps: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          resizeDelay: 0,
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: hasChartData.value,
              displayColors: false,
              callbacks: {
                label: (context) => `${context.parsed.y}%`,
              },
            },
          },
          scales: {
            x: {
              grid: {
                color: '#D9DEE7',
                drawTicks: false,
                drawBorder: false,
              },
              border: {
                display: false,
                color: 'rgba(0,0,0,0)',
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
            },
            y: {
              min: 0,
              max: 100,
              ticks: {
                stepSize: 50,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              grid: {
                color: (context) => {
                  const value = context.tick.value

                  if (value === 0 || value === 100) {
                    return 'rgba(0,0,0,0)'
                  }

                  return '#D9DEE7'
                },
                drawTicks: false,
              },
              border: {
                display: false,
              },
            },
          },
        },
      })
    }

    const renderChart = async () => {
      await nextTick()

      destroyChart()
      createChart()

      await nextTick()
      scheduleResize()
    }

    const handleWindowResize = () => {
      scheduleResize()
    }

    onMounted(async () => {
      await renderChart()

      window.addEventListener('resize', handleWindowResize)

      if (chartWrapper.value) {
        resizeObserver = new ResizeObserver(() => {
          scheduleResize()
        })

        resizeObserver.observe(chartWrapper.value)
      }

      setTimeout(() => {
        scheduleResize()
      }, 50)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleWindowResize)

      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }

      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
        resizeFrame = null
      }

      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartValues],
        async () => {
          await renderChart()
        },
        { deep: true }
    )

    return {
      displayTotal,
      chartCanvas,
      chartWrapper,
      normalizedLevels,
      reversedBarSegments,
      reversedBarGridStyle,
      getBarPartStyle,
      hasChartData,
    }
  },
}
</script>

<style scoped lang="scss">
.company-energy-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.company-energy-card__main {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
}

.company-energy-card__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.company-energy-card__legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.company-energy-card__legend-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.company-energy-card__legend-mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.company-energy-card__legend-value {
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.company-energy-card__legend-value--underlined {
  text-decoration: underline;
  cursor: pointer;
}

.company-energy-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  gap: 2px;
}

.company-energy-card__bar-part {
  height: 100%;
  min-width: 0;
}

.company-energy-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 6px;
  gap: 2px;
  font-size: 13px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.company-energy-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-energy-card__chart {
  position: relative;
  width: 100%;
  height: 122px;
  margin-top: 16px;
  min-width: 0;
}

.company-energy-card__chart canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.company-energy-card__chart-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  white-space: nowrap;
}
</style>